import { RestApiClient } from 'ifield-core-components'
import log from 'loglevel'
import _ from 'lodash'
import { FUNCTION_TYPE } from '@/constants'

/**
 * フロア取得
 * @param {Number} id ID
 * @returns {Array.<Object>|Object} データ
 */
const getIndoorLevels = async (id) => {
  const endPoint = id == null ? 'indoor_level' : `indoor_level/${id}`
  const url = RestApiClient.createApiUrl(endPoint)
  const json = await RestApiClient.executeGetApi(url)
  return json.data
}

/**
 * 初期スロットル間隔(ms)
 */
const INITIAL_THROTTLE_TIME = 30000

/**
 * 当モジュールで扱うデータが利用可能な権限タイプ
 */
const MY_FUNCTION_TYPE = FUNCTION_TYPE.INDOOR

export default {
  namespaced: true,
  state: {
    indoorLevels: [],
    throttleTime: INITIAL_THROTTLE_TIME,
    throttler: null,
  },
  persistentStates: ['throttleTime'],
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      state.indoorLevels = []
      state.throttleTime = INITIAL_THROTTLE_TIME
      state.throttler = null
    },
    /**
     * フロアを設定する
     * @param {*} state
     * @param {Array.<Object>} indoorLevels フロア
     */
    setIndoorLevels(state, indoorLevels) {
      state.indoorLevels = indoorLevels
    },
    /**
     * 特定のフロアを設定する
     * @param {*} state
     * @param {Object} indoorLevel フロア
     */
    setSpecificIndoorLevel(state, indoorLevel) {
      const targetIndex = state.indoorLevels.findIndex(
        (l) => l.id === indoorLevel.id
      )
      if (targetIndex >= 0) {
        state.indoorLevels[targetIndex] = indoorLevel
      } else {
        state.indoorLevels.push(indoorLevel)
      }
    },
    /**
     * スロットル間隔を保存する
     * @param {*} state
     * @param {Number} time スロットル間隔(ms)
     */
    setThrottleTime(state, time) {
      state.throttleTime = +time
    },
    /**
     * スロットル関数を保存する
     * @param {*} state
     * @param {Function} throttler スロットル関数
     */
    setThrottler(state, throttler) {
      state.throttler = throttler
    },
  },
  getters: {
    /**
     * フロア
     * @param {*} state
     * @returns {Array.<Object>}
     */
    indoorLevels: (state) => state.indoorLevels,
    /**
     * フロア(Map)
     * @param {*} state
     * @returns {Map}
     */
    indoorLevelMap: (state) =>
      new Map(
        state.indoorLevels.map((indoorLevel) => [indoorLevel.id, indoorLevel])
      ),
    /**
     * 特定IDのフロアを取得
     * @param {*} state
     * @param {*} getters
     * @returns {Function} IDを引数に特定IDのフロアを返す関数
     */
    getIndoorLevelById: (state, getters) => (id) =>
      getters.indoorLevelMap.get(id),
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * IndoorLevelを更新する
     */
    async updateIndoorLevels({ commit, rootGetters }) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: indoor is unavaliable. skip update')
        return
      }
      const indoorLevels = await getIndoorLevels()
      commit('setIndoorLevels', indoorLevels)
    },
    /**
     * 特定のIndoorLevelを更新する
     * @param {Number} id 対象のフロアID
     */
    async updateSpecificIndoorLevel({ commit, rootGetters }, id) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: indoor is unavaliable. skip update')
        return
      }
      const indoorLevel = await getIndoorLevels(id)
      commit('setSpecificIndoorLevel', indoorLevel)
      return indoorLevel
    },
    /**
     * フロアを更新する(スロットル)
     */
    async updateIndoorLevelsWithThrottle({ state, commit, dispatch }) {
      if (!state.throttler) {
        const throttler = _.throttle(
          () => dispatch('updateIndoorLevels'),
          state.throttleTime,
          { trailing: false }
        )
        commit('setThrottler', throttler)
      }
      return state.throttler()
    },
    /**
     * スロットル間隔更新
     * @param {Number} time スロットル間隔(ms)
     */
    updateThrottleTime({ commit }, time) {
      commit('setThrottleTime', +time)
      commit('setThrottler', null)
    },
  },
}
